import React from "react";
import { HelmetProvider } from 'react-helmet-async';
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

// Context
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

// Layout
import Header from "./components/Header";
import Footer from "./components/Footer";

// Public Pages
import HomePage from "./pages/HomePage";
import ProductsPage from "./pages/ProductsPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RegisterPage from "./pages/RegisterPage";
import SellerLandingPage from "./pages/SellerLandingPage";

import GuestCheckoutPage from './pages/GuestCheckoutPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentErrorPage from './pages/PaymentErrorPage';
import ApiManagementPage from './pages/ApiManagementPage';
// Protected Pages - Buyer
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import CheckoutSuccessPage from "./pages/CheckoutSuccessPage";
import CheckoutErrorPage from "./pages/CheckoutErrorPage";
import PaymentPage from "./pages/PaymentPage";
import OrdersPage from "./pages/OrdersPage";
import FavoritesPage from "./pages/FavoritesPage";
import ProfilePage from "./pages/ProfilePage";
import AddressesPage from "./pages/AddressesPage";
import SellerApplyPage from "./pages/SellerApplyPage";

// Protected Pages - Seller
import SellerDashboard from "./pages/SellerDashboard";
import SellerProducts from "./pages/SellerProducts";
import SellerListings from "./pages/SellerListings";
import StorePage from "./pages/StorePage";
import StoreSettings from "./pages/StoreSettings";
import ProductForm from "./pages/ProductForm";

// Protected Pages - Admin
import AdminDashboard from "./pages/AdminDashboard";
import AdminApplications from "./pages/AdminApplications";
import AdminCargoPage from "./pages/AdminCargoPage";
import AdminOrders from "./pages/AdminOrders";
import AdminCommissions from "./pages/AdminCommissions";
import AdminPayouts from "./pages/AdminPayouts";
import AdminProducts from "./pages/AdminProducts";
import AdminVendors from "./pages/AdminVendors";
import AdminViolations from "./pages/AdminViolations";
import SellerMessages from "./pages/SellerMessages";
import VehiclePage from "./pages/VehiclePage";

// Protected Route Component
const ProtectedRoute = ({ children, requiredType = null, allowedTypes = null }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredType && user?.user_type !== requiredType) {
    return <Navigate to="/" replace />;
  }

  if (allowedTypes && !allowedTypes.includes(user?.user_type)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Layout with Header and Footer
const MainLayout = ({ children }) => {
  return (
    <>
      <Header />
      <main className="min-h-screen">{children}</main>
      <Footer />
    </>
  );
};

// Auth Layout (no header/footer)
const AuthLayout = ({ children }) => {
  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes with Main Layout */}
      <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
      <Route path="/products" element={<MainLayout><ProductsPage /></MainLayout>} />
      <Route path="/urunler" element={<MainLayout><ProductsPage /></MainLayout>} />
      <Route path="/products/:id" element={<MainLayout><ProductDetailPage /></MainLayout>} />
      <Route path="/urun/:slug" element={<MainLayout><ProductDetailPage /></MainLayout>} />
      <Route path="/seller" element={<MainLayout><SellerLandingPage /></MainLayout>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<AuthLayout><LoginPage /></AuthLayout>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/guest-checkout" element={<MainLayout><GuestCheckoutPage /></MainLayout>} />
      <Route path="/odeme-basarili" element={<PaymentSuccessPage />} />
      <Route path="/odeme-hatasi" element={<PaymentErrorPage />} />
      <Route path="/sistem-yonetimi" element={<ApiManagementPage />} />

      <Route path="/register" element={<AuthLayout><RegisterPage /></AuthLayout>} />
      
      {/* Protected Routes - Any Authenticated User */}
      <Route path="/cart" element={
        <MainLayout>
          <ProtectedRoute>
            <CartPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/checkout" element={
        <MainLayout>
          <ProtectedRoute>
            <CheckoutPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/checkout/success" element={
        <MainLayout>
          <ProtectedRoute>
            <CheckoutSuccessPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/payment" element={<MainLayout><ProtectedRoute><PaymentPage /></ProtectedRoute></MainLayout>} />
      <Route path="/checkout/error" element={
        <MainLayout>
          <ProtectedRoute>
            <CheckoutErrorPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/orders" element={
        <MainLayout>
          <ProtectedRoute>
            <OrdersPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/favorites" element={
        <MainLayout>
          <ProtectedRoute>
            <FavoritesPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/profile" element={
        <MainLayout>
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/addresses" element={
        <MainLayout>
          <ProtectedRoute>
            <AddressesPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/seller/apply" element={
        <MainLayout>
          <ProtectedRoute>
            <SellerApplyPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      
      {/* Public Store Page */}
      <Route path="/store/:slug" element={
        <MainLayout>
          <StorePage />
        </MainLayout>
      } />

      {/* Seller Routes */}
      <Route path="/seller/dashboard" element={
        <MainLayout>
          <ProtectedRoute allowedTypes={['seller', 'admin']}>
            <SellerDashboard />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/seller/products" element={
        <MainLayout>
          <ProtectedRoute allowedTypes={['seller', 'admin']}>
            <SellerProducts />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/seller/listings" element={
        <MainLayout>
          <ProtectedRoute allowedTypes={['seller', 'admin']}>
            <SellerListings />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/seller/store-settings" element={
        <MainLayout>
          <ProtectedRoute allowedTypes={['seller', 'admin']}>
            <StoreSettings />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/seller/messages" element={
        <MainLayout>
          <ProtectedRoute>
            <SellerMessages />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/seller/products/new" element={
        <MainLayout>
          <ProtectedRoute allowedTypes={['seller', 'admin']}>
            <ProductForm />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/seller/products/:id/edit" element={
        <MainLayout>
          <ProtectedRoute allowedTypes={['seller', 'admin']}>
            <ProductForm />
          </ProtectedRoute>
        </MainLayout>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminDashboard />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/admin/applications" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminApplications />
          </ProtectedRoute>
        </MainLayout>
      } />
      
      <Route path="/admin/products" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminProducts />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/admin/stores" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminVendors />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/admin/orders" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminOrders />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/admin/commissions" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminCommissions />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/admin/payouts" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminPayouts />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/admin/cargo" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminCargoPage />
          </ProtectedRoute>
        </MainLayout>
      } />
      <Route path="/admin/violations" element={
        <MainLayout>
          <ProtectedRoute requiredType="admin">
            <AdminViolations />
          </ProtectedRoute>
        </MainLayout>
      } />

      {/* Vehicle SEO Routes */}
      <Route path="/arac/:brandSlug" element={<MainLayout><VehiclePage /></MainLayout>} />
      <Route path="/arac/:brandSlug/:modelSlug" element={<MainLayout><VehiclePage /></MainLayout>} />
      <Route path="/arac/:brandSlug/:modelSlug/:yearRange" element={<MainLayout><VehiclePage /></MainLayout>} />
      <Route path="/arac/:brandSlug/:modelSlug/:yearRange/:partSlug" element={<MainLayout><VehiclePage /></MainLayout>} />
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
